// SVG Child Tags
// tags that are specific children of svg (https://developer.mozilla.org/en-US/docs/Web/SVG/Element)
// if the tag is allowable elsewhere, undesireable, or deprecated, do not list it here
// - removed (not applicable to our use case): discard
// - removed (invalid): color-profile, hatch, hatchpath
// - removed (deprecated): cursor, font, font-face, font-face-format, font-face-name, font-face-src,
//   font-face-uri, glyph, glyphhref, hkern, missing-glyph, tref
// - removed (already defined as HTML tag): a, font, script, style, title
enum SvgTags {
  ANIMATE = 'animate',
  ANIMATEMOTION = 'animateMotion',
  ANIMATETRANSFORM = 'animateTransform',
  CIRCLE = 'circle',
  CLIPPATH = 'clipPath',
  DEFS = 'defs',
  DESC = 'desc',
  ELLIPSE = 'ellipse',
  FEBLEND = 'feBlend',
  FECOLORMATRIX = 'feColorMatrix',
  FECOMPONENTTRANSFER = 'feComponentTransfer',
  FECOMPOSITE = 'feComposite',
  FECONVOLVEMATRIX = 'feConvolveMatrix',
  FEDIFFUSELIGHTING = 'feDiffuseLighting',
  FEDISPLACEMENTMAP = 'feDisplacementMap',
  FEDISTANTLIGHT = 'feDistantLight',
  FEDROPSHADOW = 'feDropShadow',
  FEFLOOD = 'feFlood',
  FEFUNCA = 'feFuncA',
  FEFUNCB = 'feFuncB',
  FEFUNCG = 'feFuncG',
  FEFUNCR = 'feFuncR',
  FEGAUSSIANBLUR = 'feGaussianBlur',
  FEIMAGE = 'feImage',
  FEMERGE = 'feMerge',
  FEMERGENODE = 'feMergeNode',
  FEMORPHOLOGY = 'feMorphology',
  FEOFFSET = 'feOffset',
  FEPOINTLIGHT = 'fePointLight',
  FESPECULARLIGHTING = 'feSpecularLighting',
  FESPOTLIGHT = 'feSpotLight',
  FETILE = 'feTile',
  FETURBULENCE = 'feTurbulence',
  FILTER = 'filter',
  FOREIGNOBJECT = 'foreignObject',
  G = 'g',
  IMAGE = 'image',
  LINE = 'line',
  LINEARGRADIENT = 'linearGradient',
  MARKER = 'marker',
  MASK = 'mask',
  METADATA = 'metadata',
  MPATH = 'mpath',
  PATH = 'path',
  PATTERN = 'pattern',
  POLYGON = 'polygon',
  POLYLINE = 'polyline',
  RADIALGRADIENT = 'radialGradient',
  RECT = 'rect',
  SET = 'set',
  STOP = 'stop',
  SWITCH = 'switch',
  SYMBOL = 'symbol',
  TEXT = 'text',
  TEXTPATH = 'textPath',
  TSPAN = 'tspan',
  USE = 'use',
  VIEW = 'view',
}

// source: https://developer.mozilla.org/en-US/docs/Web/SVG/Element
// all event attributes are purposely ommitted
const animAdditionAttrs = 'additive accumulate'.split(' ');
const animAttrTargetAttrs = 'attributeName'.split(' '); // removed (deprecated): attributeType
const animTimingAttrs = 'begin dur end min max restart repeatCount repeatDur fill'.split(' ');
const animValueAttrs =
  'accelerate autoReverse calcMode decelerate values keyTimes keySplines from to by'.split(' '); // (missing doc): autoReverse, accelerate, decelerate
const conditionalProcessingAttrs = 'requiredExtensions systemLanguage'.split(' '); // removed (deprecated): requiredFeatures; (missing doc): requiredExtensions
const coreAttrs = 'id lang tabindex'.split(' ');
const filterPrimitiveAttrs = 'height result width x y'.split(' ');
const presentationAttrs =
  'alignment-baseline baseline-shift clip-path clip-rule color color-interpolation color-interpolation-filters color-rendering cursor direction display dominant-baseline fill fill-opacity fill-rule filter flood-color flood-opacity font-family font-size font-size-adjust font-stretch font-style font-variant font-weight image-rendering letter-spacing lighting-color marker-end marker-mid marker-start mask opacity overflow pointer-events shape-rendering stop-color stop-opacity stroke stroke-dasharray stroke-dashoffset stroke-linecap stroke-linejoin stroke-miterlimit stroke-opacity stroke-width text-anchor text-decoration text-rendering transform transform-origin unicode-bidi vector-effect visibility word-spacing writing-mode'.split(
    ' ',
  ); // removed (deprecated): clip, color-profile, enable-background, glyph-orientation-horizontal glyph-orientation-vertical, kerning; (missing doc): color-rendering
const styleAttrs = 'class style'.split(' ');
const transferFunctionAttrs = 'amplitude exponent intercept offset tableValues type'.split(' '); // removed (deprecated): slope; (missing doc): offset
const xLinkAttrs: string[] = []; // all are deprecated or invalid

// build attributes for each tag based on docs
const svgAttrs = {
  [SvgTags.ANIMATE]: [
    ...animTimingAttrs,
    ...animValueAttrs,
    ...coreAttrs,
    ...styleAttrs,
    ...'attributeName additive accumulate'.split(' '),
  ],
  [SvgTags.ANIMATEMOTION]: [
    ...animTimingAttrs,
    ...animValueAttrs,
    ...coreAttrs,
    ...styleAttrs,
    ...'attributeName additive accumulate'.split(' '),
    ...'keyPoints path rotate'.split(' '),
  ],
  [SvgTags.ANIMATETRANSFORM]: [
    ...animAdditionAttrs,
    ...animAttrTargetAttrs,
    ...animTimingAttrs,
    ...animValueAttrs,
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...xLinkAttrs,
    ...'by from to type'.split(' '),
  ],
  [SvgTags.CIRCLE]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'cx cy pathLength r'.split(' '),
  ],
  [SvgTags.CLIPPATH]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'clipPathUnits'.split(' '),
  ],
  [SvgTags.DEFS]: [...coreAttrs, ...presentationAttrs, ...styleAttrs],
  [SvgTags.DESC]: [...coreAttrs, ...styleAttrs],
  [SvgTags.ELLIPSE]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'cx cy pathLength rx ry'.split(' '),
  ],
  [SvgTags.FEBLEND]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in in2 mode'.split(' '),
  ],
  [SvgTags.FECOLORMATRIX]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in type values'.split(' '),
  ],
  [SvgTags.FECOMPONENTTRANSFER]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in'.split(' '),
  ],
  [SvgTags.FECOMPOSITE]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in in2 k1 k2 k3 k4 operator'.split(' '),
  ],
  [SvgTags.FECONVOLVEMATRIX]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'bias divisor edgeMode in kernelMatrix order preserveAlpha targetX targetY'.split(' '), // removed (deprecated): kernelUnitLength
  ],
  [SvgTags.FEDIFFUSELIGHTING]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'diffuseConstant in surfaceScale'.split(' '), // removed (deprecated): kernelUnitLength
  ],
  [SvgTags.FEDISPLACEMENTMAP]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in in2 scale xChannelSelector yChannelSelector'.split(' '),
  ],
  [SvgTags.FEDISTANTLIGHT]: [...coreAttrs, ...'azimuth elevation'.split(' ')],
  [SvgTags.FEDROPSHADOW]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'dx dy stdDeviation'.split(' '),
  ],
  [SvgTags.FEFLOOD]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'flood-color flood-opacity'.split(' '),
  ],
  [SvgTags.FEFUNCA]: [...coreAttrs, ...transferFunctionAttrs],
  [SvgTags.FEFUNCB]: [...coreAttrs, ...transferFunctionAttrs],
  [SvgTags.FEFUNCG]: [...coreAttrs, ...transferFunctionAttrs],
  [SvgTags.FEFUNCR]: [...coreAttrs, ...transferFunctionAttrs],
  [SvgTags.FEGAUSSIANBLUR]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in stdDeviation edgeMode'.split(' '),
  ],
  [SvgTags.FEIMAGE]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...xLinkAttrs,
    ...'preserveAspectRatio'.split(' '), // removed (deprecated): xlink:href
  ],
  [SvgTags.FEMERGE]: [...coreAttrs, ...presentationAttrs, ...styleAttrs, ...filterPrimitiveAttrs],
  [SvgTags.FEMERGENODE]: [
    ...coreAttrs,
    ...'in'.split(' '), // removed (deprecated): xlink:href
  ],
  [SvgTags.FEMORPHOLOGY]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in operator radius'.split(' '),
  ],
  [SvgTags.FEOFFSET]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'dx dy in'.split(' '),
  ],
  [SvgTags.FEPOINTLIGHT]: [...coreAttrs, ...'x y z'.split(' ')],
  [SvgTags.FESPECULARLIGHTING]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in surfaceScale specularConstant specularExponent'.split(' '), // removed (deprecated): kernelUnitLength
  ],
  [SvgTags.FESPOTLIGHT]: [
    ...coreAttrs,
    ...'limitingConeAngle pointsAtX pointsAtY pointsAtZ specularExponent x y z'.split(' '),
  ],
  [SvgTags.FETILE]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'in'.split(' '),
  ],
  [SvgTags.FETURBULENCE]: [
    ...coreAttrs,
    ...filterPrimitiveAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'baseFrequency numOctaves seed stitchTiles type'.split(' '),
  ],
  [SvgTags.FILTER]: [
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...xLinkAttrs,
    ...'filterUnits height primitiveUnits width x y'.split(' '), // removed (deprecated): filterRes, xlink:href
  ],
  [SvgTags.FOREIGNOBJECT]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'height width x y'.split(' '),
  ],
  [SvgTags.G]: [...conditionalProcessingAttrs, ...coreAttrs, ...presentationAttrs, ...styleAttrs],
  [SvgTags.IMAGE]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...xLinkAttrs,
    ...styleAttrs,
    ...'crossorigin height href preserveAspectRatio width x y'.split(' '), // removed (deprecated): xlink:href
  ],
  [SvgTags.LINE]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'pathLength x1 x2 y1 y2'.split(' '),
  ],
  [SvgTags.LINEARGRADIENT]: [
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...xLinkAttrs,
    ...'gradientUnits gradientTransform href spreadMethod x1 x2 y1 y2'.split(' '), // removed (deprecated): xlink:href
  ],
  [SvgTags.MARKER]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'markerHeight markerUnits markerWidth orient preserveAspectRatio refX refY viewBox'.split(
      ' ',
    ),
  ],
  [SvgTags.MASK]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'height maskContentUnits maskUnits width x y'.split(' '),
  ],
  [SvgTags.METADATA]: [...coreAttrs],
  [SvgTags.MPATH]: [...coreAttrs, ...xLinkAttrs],
  [SvgTags.PATH]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'d pathLength'.split(' '),
  ],
  [SvgTags.PATTERN]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'height href patternContentUnits patternTransform patternUnits preserveAspectRatio viewBox width x y'.split(
      ' ',
    ), // removed (deprecated): xlink:href
  ],
  [SvgTags.POLYGON]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'points pathLength'.split(' '),
  ],
  [SvgTags.POLYLINE]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'points pathLength'.split(' '),
  ],
  [SvgTags.RADIALGRADIENT]: [
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...xLinkAttrs,
    ...'cx cy fr fx fy gradientUnits gradientTransform href r spreadMethod'.split(' '), // removed (deprecated): xlink:href
  ],
  [SvgTags.RECT]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'height pathLength rx ry width x y'.split(' '),
  ],
  [SvgTags.SET]: [...animTimingAttrs, ...coreAttrs, ...styleAttrs, ...'attributeName'.split(' ')],
  [SvgTags.STOP]: [
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'offset stop-color stop-opacity'.split(' '), // (missing doc): offset
  ],
  [SvgTags.SWITCH]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'transform'.split(' '),
  ],
  [SvgTags.SYMBOL]: [
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'height preserveAspectRatio refX refY viewBox width x y'.split(' '),
  ],
  [SvgTags.TEXT]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'dx dy lengthAdjust rotate textLength x y'.split(' '),
  ],
  [SvgTags.TEXTPATH]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...xLinkAttrs,
    ...'href lengthAdjust method path side spacing startOffset textLength'.split(' '), // removed (deprecated): xlink:title
  ],
  [SvgTags.TSPAN]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'dx dy lengthAdjust rotate textLength x y'.split(' '),
  ],
  [SvgTags.USE]: [
    ...conditionalProcessingAttrs,
    ...coreAttrs,
    ...presentationAttrs,
    ...styleAttrs,
    ...'height href width x y'.split(' '), // removed (deprecated): xlink:href, xlink:title
  ],
  [SvgTags.VIEW]: [
    ...coreAttrs,
    ...'preserveAspectRatio viewBox'.split(' '), // removed (deprecated): viewTarget zoomAndPan
  ],
};

export const svgChildTags = Object.keys(svgAttrs).sort();
export const svgChildTagsAll = [...svgChildTags, 'a', 'font', 'style', 'title'].sort(); // 'script' has been removed
export const svgElements = (Object.keys(SvgTags) as Array<keyof typeof SvgTags>).map(
  (key) => `${SvgTags[key]}[${[...new Set(svgAttrs[SvgTags[key]])].sort().join('|')}]`, // unique, sorted attributes
);
